import { KatapultGeometry } from 'katapult-toolbox';

/**
 * A [latitude, longitude] pair.
 * @typedef {[number, number]} Location
 */

export const MAP_ANNOTATION_LEADER_ENDPOINTS = [
  { value: 'none', label: 'None' },
  { value: 'dot', label: 'Dot' },
  { value: 'rounded', label: 'Rounded Arrow' },
  { value: 'sharp', label: 'Sharp Arrow' }
];

export const MAP_ANNOTATION_LEADER_ENDPOINT_SCALE = { default: 1, min: 0.5, max: 3, step: 0.1 };

// Thickness is in print SVG units (1/96"), so the default keeps the width leaders had before it was configurable.
export const MAP_ANNOTATION_LEADER_THICKNESS = { default: 3, min: 0.5, max: 10, step: 0.5 };

// Corner radius of an annotation's text box in print SVG units (1/96"), small enough to keep short boxes from becoming pills.
export const MAP_ANNOTATION_BOX_CORNER_RADIUS = 3;

function ClampToRange(value, { default: fallback, min, max }) {
  const number = parseFloat(value);
  return Number.isFinite(number) ? Math.min(Math.max(number, min), max) : fallback;
}

/**
 * Resolves a leader's stored endpoint to one of the supported styles.
 * Leaders saved before the styles were configurable stored 'arrow' for the rounded arrowhead and nothing for the dot.
 * @param {string} [endPoint] The value stored on the leader.
 * @returns {string} One of the values in MAP_ANNOTATION_LEADER_ENDPOINTS.
 */
export function GetMapAnnotationLeaderEndpoint(endPoint) {
  if (endPoint == 'arrow') return 'rounded';
  return MAP_ANNOTATION_LEADER_ENDPOINTS.some((option) => option.value == endPoint) ? endPoint : 'dot';
}

/**
 * Resolves a leader's stored endpoint size multiplier, clamped to the range the editors offer.
 * Leaders saved before the size was configurable have no value and keep the original size.
 * @param {number|string} [scale] The value stored on the leader.
 * @returns {number} A multiplier within the supported range.
 */
export function GetMapAnnotationLeaderEndpointScale(scale) {
  return ClampToRange(scale, MAP_ANNOTATION_LEADER_ENDPOINT_SCALE);
}

/**
 * Resolves a leader's stored line thickness, clamped to the range the editors offer.
 * Leaders saved before the thickness was configurable have no value and keep the original width.
 * @param {number|string} [thickness] The value stored on the leader.
 * @returns {number} A thickness within the supported range.
 */
export function GetMapAnnotationLeaderThickness(thickness) {
  return ClampToRange(thickness, MAP_ANNOTATION_LEADER_THICKNESS);
}

// Ids of a text annotation's additional leaders that can be seen.
export function GetMapAnnotationAdditionalLeaderIds(leader) {
  if (!leader || ['angle', 'length'].includes(leader.type)) return [];
  return Object.keys(leader.additional ?? {}).filter((id) => leader.additional[id]?.points?.length >= 2);
}

// Annotation data whose leader is the given additional leader (or the main one for null), styled like the main leader.
export function GetMapAnnotationDataForLeader(data, leaderId = null) {
  if (leaderId == null) return data;
  const path = data?.leader?.additional?.[leaderId];
  if (!path) return { ...data, leader: null };
  const leader = { ...data.leader, points: path.points, edge: path.edge, percent: path.percent };
  delete leader.additional;
  delete leader.snapToNode;
  return { ...data, leader };
}

/**
 * Builds the dotted connector that ties a dimension's label back to the dimension it measures.
 * The label may be dragged anywhere, so the connector shows which dimension the number belongs to once
 * the label no longer sits on the dimension line.
 *
 * @param {Location[]} corners The label box corners in order: top left, top right, bottom right, bottom left.
 * @param {Location[]} linePoints The points of the dimension line: the two ends of a length, or every point along an angle's arc.
 * @param {string} [type] The dimension's leader type. Angles aim their connector differently from inside the arc.
 * @returns {?Location[]} The connector's two endpoints, or null when the label already sits on the dimension line.
 */
export function GetDimensionLabelConnector(corners, linePoints, type) {
  if (corners?.length != 4 || !(linePoints?.length >= 2)) return null;

  const srid = KatapultGeometry.LatLongToXY(linePoints[0][0], linePoints[0][1]).srid;
  const line = linePoints.map(([latitude, longitude]) => KatapultGeometry.LatLongToXY(latitude, longitude, srid));
  const segments = line.slice(1).map((point, i) => [line[i], point]);
  const box = corners.map(([latitude, longitude]) => KatapultGeometry.LatLongToXY(latitude, longitude, srid));
  const edges = box.map((corner, i) => [corner, box[(i + 1) % box.length]]);

  // The dimension line runs across the label, so the number is already tied to its dimension.
  if (segments.some((segment) => edges.some((edge) => KatapultGeometry.Intersect(...segment, ...edge)))) return null;

  const center = KatapultGeometry.CalcMidPointXY(box[0], box[2]);
  let target = type == 'angle' ? getArcConnectorTarget(center, line) : null;
  // Otherwise aim the connector at the closest point on the dimension so it reads as the shortest tie back to the line.
  if (!target) {
    let shortestDistance = Infinity;
    segments.forEach((segment) => {
      const snap = KatapultGeometry.SnapToLineXY(center, ...segment);
      const distance = Math.hypot(snap.x - center.x, snap.y - center.y);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        target = { x: snap.x, y: snap.y };
      }
    });
  }

  // Start the connector at the label's edge instead of its center so it never draws through the number.
  // Without a crossing the closest point is inside the label, which again needs no connector.
  const start = edges.reduce((found, edge) => found || KatapultGeometry.Intersect(center, target, ...edge), null);
  if (!start) return null;

  return [toLocation(start, srid), toLocation(target, srid)];
}

/**
 * Picks where a connector from a label meets an angle's arc. Only a label directly outward of the arc, within its sweep,
 * reads well aimed at the closest point. Anywhere else the closest point is an end of the arc, so these labels aim at
 * the arc's center instead.
 *
 * @param {{x: number, y: number}} point The label's center.
 * @param {{x: number, y: number}[]} arc The points along the arc.
 * @returns {?{x: number, y: number}} The target, or null when the label is directly outward of the arc and should use the closest point.
 */
function getArcConnectorTarget(point, arc) {
  if (arc.length < 3) return null;
  const start = arc[0];
  const middle = arc[Math.floor(arc.length / 2)];
  const end = arc[arc.length - 1];

  // The arc's vertex is the center of the circle through its ends and middle, solved relative to the start
  // because projected coordinates are large enough to lose precision when squared.
  const bx = middle.x - start.x;
  const by = middle.y - start.y;
  const cx = end.x - start.x;
  const cy = end.y - start.y;
  const d = 2 * (bx * cy - by * cx);
  if (!d) return null;
  const vertex = {
    x: start.x + (cy * (bx * bx + by * by) - by * (cx * cx + cy * cy)) / d,
    y: start.y + (bx * (cx * cx + cy * cy) - cx * (bx * bx + by * by)) / d
  };
  const radius = Math.hypot(start.x - vertex.x, start.y - vertex.y);

  const angleOf = (p) => Math.atan2(p.y - vertex.y, p.x - vertex.x);
  const wrap = (angle) => Math.atan2(Math.sin(angle), Math.cos(angle));
  const startAngle = angleOf(start);
  let sweep = wrap(angleOf(end) - startAngle);
  // Wrapping takes the short way around, so turn it back when the arc runs the long way through its middle.
  const towardMiddle = wrap(angleOf(middle) - startAngle);
  if (Math.sign(sweep) != Math.sign(towardMiddle)) sweep -= Math.sign(sweep) * 2 * Math.PI;
  const midAngle = startAngle + sweep / 2;

  const outsideCircle = Math.hypot(point.x - vertex.x, point.y - vertex.y) >= radius;
  const withinSweep = Math.abs(wrap(angleOf(point) - midAngle)) <= Math.abs(sweep) / 2;
  if (outsideCircle && withinSweep) return null;
  return { x: vertex.x + radius * Math.cos(midAngle), y: vertex.y + radius * Math.sin(midAngle) };
}

/**
 * @param {{x: number, y: number}} point
 * @param {string|number} srid
 * @returns {Location}
 */
function toLocation(point, srid) {
  const latLong = KatapultGeometry.XyToLatLong(point.x, point.y, srid);
  return [latLong.lat, latLong.long];
}

/**
 * Polyline options that draw a label connector as a dotted line. The caller supplies the map.
 * @param {string} color
 * @returns {Object} Options to pass to a google.maps.Polyline.
 */
export function GetLabelConnectorPolylineOptions(color) {
  return {
    clickable: false,
    strokeColor: color,
    // The dots are drawn as repeated symbols, so the line itself is left invisible.
    strokeOpacity: 0,
    strokeWeight: 2,
    icons: [
      { icon: { path: 'M 0,-1 0,1', strokeColor: color, strokeOpacity: 1, strokeWeight: 2, scale: 2 }, offset: '0px', repeat: '10px' }
    ]
  };
}

/**
 * Drops a point onto the axis a connection sets for a dimension. The connection only fixes the axis's direction, so
 * the axis runs on past both ends of the connection and the perpendicular keeps extending with the dimension.
 *
 * @param {Location} point The location to project.
 * @param {Location[]} axis Two locations on the axis.
 * @returns {{point: Location, position: number}} The point on the axis perpendicular to the given point, and how far
 * along the axis it landed as a fraction of the axis, so anything outside 0 to 1 is past the connection itself.
 */
export function ProjectOntoAxis(point, axis) {
  const p = KatapultGeometry.LatLongToXY(point[0], point[1]);
  const start = KatapultGeometry.LatLongToXY(axis[0][0], axis[0][1], p.srid);
  const end = KatapultGeometry.LatLongToXY(axis[1][0], axis[1][1], p.srid);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return { point: axis[0], position: 0 };
  const position = ((p.x - start.x) * dx + (p.y - start.y) * dy) / lengthSquared;
  return { point: toLocation({ x: start.x + dx * position, y: start.y + dy * position }, p.srid), position };
}

/**
 * Builds the run of a connection's axis that carries on past the connection to reach a dimension measured from it.
 * Without it a dimension pulled beyond the end of its connection appears to float free of the line it measures from.
 *
 * @param {Location[]} axis The two endpoints of the connection segment that set the dimension's axis.
 * @param {Location} start The dimension's start point.
 * @returns {?Location[]} The extension's two endpoints, or null while the dimension still starts on the connection.
 */
export function GetLengthDimensionAxisExtension(axis, start) {
  if (axis?.length != 2 || start?.length != 2) return null;
  const { point, position } = ProjectOntoAxis(start, axis);
  if (position >= 0 && position <= 1) return null;
  return [position < 0 ? axis[0] : axis[1], point];
}
